/* eslint no-console: 'off' */

import { DataRequestBase } from './data_request_base.js';
import { settings } from './settings.js';

const log_modules = [
  'app',
  'account',
  'accept-share',
  'checklist',
  'control:autocomplete',
  'control:autocomplete:event',
  'control:goingwith',
  'control:image-slider',
  'control:list',
  'control:location',
  'control:locsearch',
  'control:loctype',
  'control:popuplist:event',
  'control:profilebutton',
  'control:weather',
  'data',
  'data:perf',
  'eventrace',
  'focus',
  'food-menu',
  'gallery:open',
  'history',
  'load',
  'map',
  'map:bounds',
  'map:close',
  'map:controls-bar',
  'map:googlemaps',
  'map:googlemaps:popup',
  'map:googlemaps:marker',
  'map:info-window',
  'map:leaflet',
  'map:locadd',
  'map:locfilter-by-type',
  'map:locfilter-on-map',
  'map:marker',
  'map:marker:leaflet',
  'map:media',
  'map:open',
  'map:polyline',
  'map:polyline:googlemaps',
  'map:polyline:leaflet',
  'map:search',
  'map:sidepanel',
  'map:util:latlng-to-coords',
  'navigation',
  'loc:delete-dialog',
  'loc:request-deletition-dialog',
  'loc:share-dialog',
  'locpage',
  'locpage:close',
  'locpage:open',
  'locpage:picpick',
  'locview',
  'panel',
  'panel:switch',
  'picupload',
  'preferences',
  'profile',
  'settings',
  'storage',
  'request',
  'review',
  'trip',
  'trip-planning',
  'ui',
  'ws',
];

/**
 * Logger.
 */
class Logger {
  constructor() {
    this.buf = [];
    this.bufLimit = 3;
    this.flushTimeout = null;
  }

  log(msg) {
    // No logs on production.
    if (settings.consoleLogDisabled) {
      return;
    }

    console.log(msg);

    let labels_str = msg.match(/(?:\[.+?\])*/)[0];
    let labels = labels_str.match(/\[.+?\]/g);
    if (!labels) {
      this.error(`[logger] no labels in '${msg}' message`);
      return;
    }

    for (let label of labels) {
      if (!log_modules.find(m => `[${m}]` == label)) {
        this.error(
          `[logger] unrecognized '${label}' label in '${msg}' message`
        );
      }
    }

    this.#maybeLogStack(msg);
  }

  assert(expr, msg) {
    if (settings.throwOnAssertOrError) {
      if (!expr) {
        throw new Error(msg);
      }
    }
    if (!settings.consoleDisabled) {
      console.assert(expr, msg);
      if (!expr) {
        this.#maybeLogStack(msg);
      }
    }

    return (
      !expr &&
      this.collect({
        component: 'www',
        severity: 'warn',
        detail: {
          message: msg,
        },
      })
    );
  }

  error(...args) {
    if (settings.throwOnAssertOrError) {
      let argslist = [...args];
      let farg = argslist[0];
      let larg = argslist[argslist.length - 1];
      let message = (!(farg instanceof Error) && farg) || 'Unhandled exception';
      let cause = larg instanceof Error && larg;
      let options = cause && { cause };
      throw new Error(message, options);
    }

    if (!settings.consoleDisabled) {
      console.error(...args);
    }

    let [message, error] = [...args];
    if (message instanceof Error) {
      error = message;
      message = error.message;
    }

    return this.collect({
      component: 'www',
      severity: 'error',
      detail: {
        message,
        error,
      },
    });
  }

  collect(log) {
    if (settings.analyticsDisabled) {
      return null;
    }

    // Do not collect errors in case of an error storm.
    if (this.buf.length > this.bugLimit) {
      // eslint-disable-next-line no-console
      console.log(`Log buffer limit is exceeded, skip log collection`);
      return null;
    }

    this.buf.push(log);

    if (!this.flushPromise) {
      this.flushPromise = new Promise(resolve =>
        setTimeout(() => this.flush().then(resolve), 1000)
      );
    }
    return this.flushPromise;
  }

  flush() {
    const logs = this.buf;

    this.flushPromise = null;
    this.buf = [];

    if (!this.requester) {
      this.requester = new DataRequestBase();
    }

    return (
      this.requester.request({
        module: 'service',
        action: 'log',
        logs,
      }) || Promise.resolve()
    );
  }

  #maybeLogStack(msg) {
    if (settings.consoleStackDisabled) {
      return;
    }
    let stack_labels = msg.replace(
      /\[.+?\]/g,
      l => `[${l.replace(/(\[|\])/g, '')}:stack]`
    );

    let stack = new Error().stack
      .split('\n')
      .filter(l => l != 'Error') // Chrome new Error().stack starts from 'Error'
      .slice(2)
      .map(l =>
        l.replace(
          /@data:text\/html,\S+?(?:\s|:)(\d+:\d+)/,
          (match, p) => `@dataurl_placeholder:${p}`
        )
      )
      .join('\n'); // strip out logger.log() and log() from stack

    // Note, apparently Chrome 96 doesn't remove data urls from the stack under
    // the hood, for example, it keeps |length| unchanged. Apparently it also
    // makes Chrome 96 to hang when processing huge strings in the console
    // (presumably when stack length is large enough).
    console.debug(`${stack_labels}\n${stack}`);
  }
}

const logger = new Logger();

export const log = (...args) => logger.log(...args);
export const assert = (...args) => logger.assert(...args);
export const error = (...args) => logger.error(...args);
